import React, { useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useDemoStatus } from '@/hooks/useDemoStatus';

/**
 * Phase 84.22 · Dashboard banner shown while the user has any demo rows.
 * Clicking "Clear demo data" opens a confirmation dialog and wipes every
 * is_demo=true row (accounts, transactions, wealth, liabilities, goals,
 * recurring). Non-destructive to real data.
 */
export function DemoDataBanner() {
  const { status, loading, clearDemo } = useDemoStatus();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [clearing, setClearing] = useState(false);

  if (!status || !status.has_demo) return null;

  const totalRows = Object.values(status.demo_counts || {}).reduce((a, b) => a + (b || 0), 0);

  const handleClear = async () => {
    setClearing(true);
    try {
      const res = await clearDemo();
      if (res?.ok) {
        toast.success('Demo data cleared. Reloading your clean overview…');
        setConfirmOpen(false);
        // v3.7.3 Phase 84.28.11 — hard reload so the dashboard briefing
        // hero tabs (and every other cached slice) refetch from scratch.
        setTimeout(() => { window.location.assign('/dashboard'); }, 500);
      } else {
        toast.error('Could not clear demo data. Please try again.');
      }
    } finally {
      setClearing(false);
    }
  };

  return (
    <>
      <div
        className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl border border-primary/40 bg-primary/8 px-4 py-3"
        data-testid="demo-data-banner"
        role="status"
      >
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <Sparkles className="h-5 w-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
          <div className="min-w-0">
            <div className="text-sm font-semibold">You&rsquo;re viewing demo data</div>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Meet <span className="font-medium text-foreground">Priya from Dubai</span> — a sample profile with {totalRows}
              &nbsp;pre-loaded rows across accounts, transactions, wealth, loans and goals. Clear it whenever you&rsquo;re
              ready to use 2120 with your own statements.
            </p>
          </div>
        </div>
        <div className="flex gap-2 sm:shrink-0">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setConfirmOpen(true)}
            disabled={loading}
            data-testid="demo-banner-clear-btn"
          >
            Clear &amp; use my own data
          </Button>
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="demo-clear-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Clear demo data?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes every demo row from your account:{' '}
              {totalRows} entries across accounts, transactions, wealth, loans, recurring commitments and goals.
              None of your real data (statements you upload, accounts you add) is affected. This can&rsquo;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={clearing}>Keep demo</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClear}
              disabled={clearing}
              data-testid="demo-clear-confirm-btn"
            >
              {clearing ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Clearing…</>) : 'Clear demo data'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
